import React from 'react';

import styled from '@emotion/styled';
import { Box, Flex } from '@lightspeed/cirrus-core';
import Group from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Modal, { ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import Text from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { IconChevronLeft } from '@lightspeed/flame/Icon/ChevronLeft';
import get from 'lodash/get';
import moment from 'moment';

import { modalSteps } from '..';
import { errorReasons, sendAgeVerificationRequest } from '../utils';
import { manualDocumentTypes } from './Manual';
import { useEventLog } from './use-event-log';

const TextBox = styled(Box)`
  text-align: center;
  margin: 12px;
`;

const getAge = (date) => moment().diff(date, 'years');

const getValidDaysRemaining = (date) => moment(date).diff(new Date(), 'days');

const AgeVerificationPinOverride = ({
  setStepWithData,
  reject,
  resolve,
  stepData,
  defaultStepData,
}) => {
  const { submitEvent } = useEventLog();
  const [isLoading, setIsLoading] = React.useState(false);
  const [pin, setPin] = React.useState('');
  const [documentType] =
    Object.entries(manualDocumentTypes).find(
      ([, value]) => value === get(stepData, 'documentType')
    ) || [];
  const validDaysRemaining = getValidDaysRemaining(stepData.cardExpiry);
  const requiredAge = get(defaultStepData, 'requiredAge');
  return (
    <Modal className="cirrus responsive" isOpen onRequestClose={reject}>
      <ModalHeader onCloseClicked={reject}>Manager verification</ModalHeader>
      <div>
        <Box m="24px">
          <Flex className="cr-justify-around">
            <TextBox>
              <Text bodySmall bold>
                ID Type
              </Text>
              <Text heading2>{documentType || ''}</Text>
            </TextBox>
            <TextBox>
              <Text bodySmall bold>
                AGE
              </Text>
              <Text
                heading2
                color={requiredAge > getAge(stepData.dateOfBirth || '') ? 'maple-300' : ''}
              >
                {getAge(stepData.dateOfBirth || '')}
              </Text>
              {requiredAge > getAge(stepData.dateOfBirth || '') ? (
                <Text dimmed bodySmall>
                  <var>{requiredAge}</var> or older
                </Text>
              ) : null}
            </TextBox>
            <TextBox>
              <Text bodySmall bold>
                STATUS
              </Text>
              <Text heading2 color={validDaysRemaining <= 0 ? 'maple-300' : ''}>
                {validDaysRemaining > 0 ? 'Valid' : 'Expired'}
              </Text>
              <Text dimmed bodySmall>
                {validDaysRemaining > 0 ? (
                  <>
                    <var>{validDaysRemaining}</var> days
                  </>
                ) : (
                  <>
                    <var>{Math.abs(validDaysRemaining)}</var> days ago
                  </>
                )}
              </Text>
            </TextBox>
          </Flex>
        </Box>
        <Divider spacing={1} />
        <Box m="24px">
          <Text bodySmall bold>
            Manager PIN
          </Text>
          <Input
            type="password"
            autoComplete="off"
            placeholder="Manager PIN required to manually verify customer ID"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
        </Box>
      </div>
      <ModalFooter>
        <Group className="cr-justify-between">
          <Button
            onClick={() => {
              setStepWithData({
                step: modalSteps.AGE_VERIFICATION_MANUAL,
                data: {
                  ...stepData,
                },
              });
            }}
          >
            <IconChevronLeft />
            Back
          </Button>
          <Group className="cr-justify-end">
            <Button
              variant="danger"
              onClick={() => {
                submitEvent(errorReasons.DECLINED);
                reject();
              }}
            >
              Remove item
            </Button>
            <Button
              loading={isLoading}
              disabled={!pin}
              variant="primary"
              fill
              onClick={() => {
                setIsLoading(true);
                try {
                  sendAgeVerificationRequest({
                    resolve,
                    reject,
                    setStepWithData,
                    stepData: {
                      ...stepData,
                      overridePIN: pin,
                    },
                  });
                } catch (_) {
                  setIsLoading(false);
                }
              }}
            >
              Authorize
            </Button>
          </Group>
        </Group>
      </ModalFooter>
    </Modal>
  );
};

export default AgeVerificationPinOverride;
